/**
 * Yup Schema Builder
 * ==================
 * Converts FormField definitions to Yup validation schemas.
 * This enables using the global validation messages with Formik + Yup.
 */

import * as Yup from 'yup';
import { FormField, FormSchema } from './form-schema';

/**
 * Build a Yup schema from FormField definitions
 * Ensures consistent error messages across Formik and global validation system
 */
export const buildYupSchema = (schema: FormSchema): Yup.ObjectSchema<any> => {
  const yupShape: Record<string, any> = {};

  for (const field of schema.getAllFields()) {
    yupShape[field.name] = buildYupField(field);
  }

  return Yup.object().shape(yupShape);
};

/**
 * Build a single Yup field from FormField
 */
export const buildYupField = (field: FormField): Yup.Schema<any> => {
  let schema: any = null;

  switch (field.fieldType) {
    case 'email':
      schema = Yup.string().email(`${field.label} must be a valid email address`);
      break;

    case 'phone':
      schema = Yup.string().matches(
        /^\+?1?\d{9,15}$/,
        `${field.label} must be a valid phone number`
      );
      break;

    case 'url':
      schema = Yup.string().url(`${field.label} must be a valid URL`);
      break;

    case 'uuid':
      schema = Yup.string().matches(
        /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i,
        `${field.label} must be a valid UUID`
      );
      break;

    case 'integer':
      schema = Yup.number().typeError(`${field.label} must be a number`);
      if (field.options.minValue !== undefined) {
        schema = schema.min(
          field.options.minValue,
          `${field.label} must be at least ${field.options.minValue}`
        );
      }
      if (field.options.maxValue !== undefined) {
        schema = schema.max(
          field.options.maxValue,
          `${field.label} must be at most ${field.options.maxValue}`
        );
      }
      break;

    case 'boolean':
      schema = Yup.boolean();
      break;

    case 'title':
    case 'text':
      schema = Yup.string().trim();
      if (field.options.minLength !== undefined) {
        schema = schema.min(
          field.options.minLength,
          `${field.label} must be at least ${field.options.minLength} characters`
        );
      }
      if (field.options.maxLength !== undefined) {
        schema = schema.max(
          field.options.maxLength,
          `${field.label} must be at most ${field.options.maxLength} characters`
        );
      }
      break;

    case 'body':
      schema = Yup.string().trim();
      const minBodyLength = field.options.minLength || 1;
      const maxBodyLength = field.options.maxLength || 5000;
      schema = schema
        .min(
          minBodyLength,
          `${field.label} must be at least ${minBodyLength} characters`
        )
        .max(
          maxBodyLength,
          `${field.label} must be at most ${maxBodyLength} characters`
        );
      break;

    case 'dropdown':
      schema = Yup.string().oneOf(
        (field.options.choices || []).map(c => 
          typeof c === 'object' ? c.value : c
        ),
        `${field.label} must be a valid option`
      );
      break;

    case 'date':
      schema = Yup.string().typeError(`${field.label} must be a valid date`);
      break;

    case 'iso_datetime':
      schema = Yup.string().typeError(`${field.label} must be a valid datetime`);
      break;

    case 'file':
      schema = Yup.mixed();
      break;

    default:
      schema = Yup.string();
  }

  // Apply required constraint
  if (field.required) {
    schema = schema.required(`${field.label} is required`);
  } else {
    schema = schema.notRequired().nullable();
  }

  return schema;
};

/**
 * Convenience function: Build Yup schema and validate data
 */
export const validateWithYup = async (
  schema: FormSchema,
  formData: Record<string, any>
): Promise<{ isValid: boolean; errors: Record<string, string> }> => {
  const yupSchema = buildYupSchema(schema);

  try {
    await yupSchema.validate(formData, { abortEarly: false });
    return { isValid: true, errors: {} };
  } catch (error: any) {
    const errors: Record<string, string> = {};
    if (error.inner && Array.isArray(error.inner)) {
      error.inner.forEach((err: any) => {
        if (err.path && !errors[err.path]) {
          errors[err.path] = err.message;
        }
      });
    }
    return { isValid: false, errors };
  }
};
